(function () {
  'use strict';

  const DIGITISATION_FEE_VARIANT_ID = 55420858270081;
  const DIGITISATION_FEE_VARIANT_ID_STR = String(DIGITISATION_FEE_VARIANT_ID);
  const CART_PRICING_SYNCED_EVENT = 'new-logo:cart-pricing-synced';
  const CART_PRICING_SOURCE = 'new-logo-cart-pricing';

  function emitCartPricingSynced(reason) {
    document.dispatchEvent(
      new CustomEvent(CART_PRICING_SYNCED_EVENT, {
        detail: {
          reason: String(reason || ''),
          at: Date.now(),
        },
      })
    );
  }

  function toWholeNumber(value) {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function toCents(value) {
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) return 0;
      if (Number.isInteger(value)) return value;
      return Math.round(value * 100);
    }

    const raw = String(value ?? '').trim();
    if (!raw) return 0;

    // Plain digits are treated as cents (Shopify cart.js style)
    if (/^-?\d+$/.test(raw)) {
      const parsedInteger = Number.parseInt(raw, 10);
      return Number.isFinite(parsedInteger) ? parsedInteger : 0;
    }

    // Currency/decimal strings are treated as major units
    const normalized = raw.replace(/[^0-9,.-]/g, '').replace(/,/g, '');
    const parsedDecimal = Number.parseFloat(normalized);
    if (!Number.isFinite(parsedDecimal)) return 0;
    return Math.round(parsedDecimal * 100);
  }

  function resolveVatRatePercent() {
    const toggle = document.querySelector('[data-vat-toggle]');
    if (!toggle) return null;
    const parsed = Number.parseFloat(String(toggle.dataset.vatRate || '').trim());
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return parsed;
  }

  function normaliseInclusiveCostCents(cents) {
    const value = toCents(cents);
    if (value <= 0) return 0;

    const vatRatePercent = resolveVatRatePercent();
    if (vatRatePercent == null) return value;

    const multiplier = 1 + vatRatePercent / 100;
    if (!Number.isFinite(multiplier) || multiplier <= 0) return value;

    return Math.round(value / multiplier);
  }

  function grossUpCents(cents) {
    const value = Math.max(0, Math.round(Number(cents) || 0));
    if (value <= 0) return 0;
    const vatRatePercent = resolveVatRatePercent();
    if (vatRatePercent == null) return value;
    const multiplier = 1 + vatRatePercent / 100;
    if (!Number.isFinite(multiplier) || multiplier <= 0) return value;
    return Math.round(value * multiplier);
  }

  /** Mirrors vat-price-toggle.js, which stores the choice and emits no event. */
  function vatDisplayIncludesTax() {
    let mode = '';
    try {
      mode = String(window.localStorage.getItem('gsww_vat_mode') || '').trim();
    } catch (error) {
      mode = '';
    }
    if (mode !== 'inc' && mode !== 'exc') {
      const toggle = document.querySelector('[data-vat-toggle]');
      mode = String(toggle?.dataset?.vatDefaultMode || 'exc').trim();
    }
    return mode === 'inc';
  }

  function formatMoney(cents) {
    const safeCents = Math.round(Number(cents) || 0);
    const themeFormat = window.FoxTheme?.settings?.moneyFormat;
    const formatter = window.FoxTheme?.utils?.formatMoney;
    if (typeof formatter === 'function' && themeFormat) {
      try {
        const formatted = String(formatter(safeCents, themeFormat) || '').trim();
        // Guard against malformed strings like "£ 133.24.64".
        const malformed = /[£$€]\s?\d[\d,]*\.\d{2}\.\d+/.test(formatted);
        if (formatted && !malformed) {
          return formatted;
        }
      } catch (error) {
        // fall through to safe formatter
      }
    }

    const amount = safeCents / 100;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  function getLineFromItemElement(itemEl) {
    const id = String(itemEl?.id || '');
    const match = id.match(/-(\d+)$/);
    if (match) {
      const line = toWholeNumber(match[1]);
      if (line > 0) return line;
    }

    const indexInput = itemEl?.querySelector('[data-index]');
    const line = toWholeNumber(indexInput?.getAttribute('data-index') || '');
    return line > 0 ? line : null;
  }

  function buildCartItemMapByElement(itemElements, cartItems) {
    const elements = Array.isArray(itemElements) ? itemElements : [];
    const items = Array.isArray(cartItems) ? cartItems : [];
    const byKey = new Map();
    const byLine = new Map();
    const usedItemIndexes = new Set();
    const elementToItem = new Map();

    items.forEach((item, index) => {
      const key = String(item?.key || '').trim();
      if (!key || byKey.has(key)) return;
      byKey.set(key, { item, index });
    });
    items.forEach((item, index) => {
      const line = index + 1;
      if (line <= 0 || byLine.has(line)) return;
      byLine.set(line, { item, index });
    });

    // Prefer explicit key mapping if available.
    elements.forEach((itemEl) => {
      const key = String(itemEl?.getAttribute('data-cart-item-key') || '').trim();
      if (!key) return;
      const matched = byKey.get(key);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;
      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    // Next prefer explicit line index mapping (CartDrawer-Item-<line> / CartItem-<line>)
    // so hidden/auxiliary rows never shift regular item pricing.
    elements.forEach((itemEl) => {
      if (elementToItem.has(itemEl)) return;
      const line = getLineFromItemElement(itemEl);
      if (!line || line <= 0) return;
      const matched = byLine.get(line);
      if (!matched) return;
      if (usedItemIndexes.has(matched.index)) return;
      usedItemIndexes.add(matched.index);
      elementToItem.set(itemEl, matched);
    });

    const remainingElements = elements.filter((itemEl) => !elementToItem.has(itemEl));
    const remainingItems = items
      .map((item, index) => ({ item, index }))
      .filter((entry) => !usedItemIndexes.has(entry.index));

    const regularElements = [];
    const feeElements = [];
    remainingElements.forEach((itemEl) => {
      const variantId = String(itemEl?.getAttribute('data-variant-id') || '').trim();
      if (variantId === DIGITISATION_FEE_VARIANT_ID_STR) {
        feeElements.push(itemEl);
      } else {
        regularElements.push(itemEl);
      }
    });

    const regularItems = [];
    const feeItems = [];
    remainingItems.forEach((entry) => {
      if (isDigitisationFeeItem(entry.item)) {
        feeItems.push(entry);
      } else {
        regularItems.push(entry);
      }
    });

    regularElements.forEach((itemEl, index) => {
      const matched = regularItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    feeElements.forEach((itemEl, index) => {
      const matched = feeItems[index] || null;
      if (!matched) return;
      elementToItem.set(itemEl, matched);
      usedItemIndexes.add(matched.index);
    });

    return elementToItem;
  }

  function setInlinePriceText(priceElement, moneyText) {
    if (!priceElement) return;

    const regularPrice = priceElement.querySelector('.price__regular');
    if (regularPrice) {
      regularPrice.textContent = moneyText;
      return;
    }

    const preserveSelectors = ['.unit-price', '.visually-hidden', '.price__sale'];
    const preserveNodes = Array.from(priceElement.children).filter((child) =>
      preserveSelectors.some((selector) => child.matches(selector))
    );

    // Remove any pre-existing money wrappers/text fragments to avoid leftovers like ".64".
    Array.from(priceElement.childNodes).forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE && preserveNodes.includes(node)) return;
      priceElement.removeChild(node);
    });

    if (!preserveNodes.length) {
      priceElement.textContent = moneyText;
      return;
    }

    const firstPreserveNode = preserveNodes[0];
    const inlineMoney = document.createElement('span');
    inlineMoney.className = 'new-logo-inline-price';
    inlineMoney.textContent = moneyText;
    priceElement.insertBefore(inlineMoney, firstPreserveNode);
  }

  function getVisibleCartItemCount(items) {
    return (Array.isArray(items) ? items : []).reduce((sum, item) => {
      if (isDigitisationFeeItem(item)) return sum;
      const qty = toWholeNumber(item?.quantity);
      return sum + (qty > 0 ? qty : 0);
    }, 0);
  }

  function syncCartDrawerEmptyState(cart) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    // No cart means no information, which is not the same as an empty cart.
    if (!cart || !Array.isArray(cart.items)) return;

    const items = cart.items;
    const visibleItemCount = getVisibleCartItemCount(items);
    let isEmpty = visibleItemCount <= 0;

    // Mid-operation the cart is between states — a line has been rewritten and
    // the fee line may be momentarily absent. Painting the empty state from
    // that window flashed "your cart is empty" over a cart that was not, and it
    // also hides CartDrawerBody, which is what the loading curtain hangs off.
    // Never claim empty while an operation owns the cart.
    if (isEmpty && isCartOperationBusy()) isEmpty = false;

    drawer.querySelectorAll('[id^="CartDrawerEmpty-"]').forEach((el) => {
      el.classList.toggle('hidden', !isEmpty);
    });
    drawer.querySelectorAll('[id^="CartDrawerBody-"]').forEach((el) => {
      el.classList.toggle('hidden', isEmpty);
    });
    drawer.querySelectorAll('[id^="CartDrawerFooter-"]').forEach((el) => {
      el.classList.toggle('hidden', isEmpty);
    });

    drawer.querySelectorAll('cart-count[data-type="blank"]').forEach((countEl) => {
      if (isEmpty) {
        countEl.setAttribute('hidden', '');
        countEl.textContent = '(0)';
        return;
      }

      const displayCount = visibleItemCount < 100 ? String(visibleItemCount) : '99+';
      countEl.removeAttribute('hidden');
      countEl.textContent = `(${displayCount})`;
    });
  }

  function resolveCustomisationNoteAnchor(itemEl, pricesElement) {
    const lineTotalElement = itemEl?.querySelector('.cart-item__total .font-body-bolder');
    if (lineTotalElement && lineTotalElement.parentElement) {
      return { parent: lineTotalElement.parentElement, before: lineTotalElement };
    }

    const priceElement = pricesElement?.querySelector('.price');
    if (priceElement) {
      const wrapper = priceElement.closest('div');
      if (wrapper && wrapper.parentElement === pricesElement) {
        return { parent: pricesElement, before: wrapper };
      }
      if (priceElement.parentElement) {
        return { parent: priceElement.parentElement, before: priceElement };
      }
    }

    if (pricesElement) {
      return { parent: pricesElement, before: pricesElement.firstChild };
    }

    return null;
  }

  function upsertCustomisationNote(itemEl, pricesElement, extraPerItem) {
    if (!pricesElement) return;

    let note = pricesElement.querySelector('.new-logo-extra-note');
    const hasCost = extraPerItem > 0;

    if (!hasCost) {
      if (note) note.remove();
      return;
    }

    if (!note) {
      note = document.createElement('div');
      note.className = 'new-logo-extra-note text-subtext';
      note.setAttribute('data-vat-exclude', '');
      note.style.fontSize = '1.04rem';
      note.style.lineHeight = '1.1';
      note.style.display = 'block';
      note.style.textAlign = 'right';
      note.style.width = '100%';
    }

    note.style.marginTop = '-2rem';
    note.style.marginBottom = '0.3rem';

    const anchor = resolveCustomisationNoteAnchor(itemEl, pricesElement);
    if (!anchor?.parent) return;

    // Only usable as a reference node if it is actually a child of the parent
    // we are inserting into — otherwise insertBefore throws NotFoundError.
    const reference = anchor.before && anchor.before.parentNode === anchor.parent ? anchor.before : null;

    if (note.parentElement !== anchor.parent) {
      if (note.parentElement) note.parentElement.removeChild(note);
      if (reference) {
        anchor.parent.insertBefore(note, reference);
      } else {
        anchor.parent.appendChild(note);
      }
    } else if (reference && note.nextSibling !== reference) {
      anchor.parent.insertBefore(note, reference);
    }

    note.textContent = formatMoney(extraPerItem);
  }

  function getLogoCostParts(properties) {
    const source = properties || {};
    const rawBasis = String(source._logo_price_basis || '').trim().toLowerCase();
    const hasExplicitBasis = rawBasis === 'inclusive' || rawBasis === 'exclusive';
    const isInclusiveBasis = hasExplicitBasis ? rawBasis === 'inclusive' : true;
    let extraPerItem = toCents(source._logo_extra_per_item_cents);
    if (extraPerItem <= 0) {
      const positionsCount = toWholeNumber(source._logo_positions_count);
      const unitCents = toCents(source._logo_price_unit_cents);
      extraPerItem = positionsCount * unitCents;
    }

    let setupFee = toCents(source._logo_setup_fee_cents);
    if (setupFee <= 0) {
      const setupRequiredValue = String(source._logo_setup_required || source['Logo Setup Required'] || '').toLowerCase();
      const setupRequired = setupRequiredValue === 'yes' || setupRequiredValue === 'true' || setupRequiredValue === '1';
      const method = String(source._logo_method || source['Logo Method'] || '').toLowerCase();
      if (setupRequired && method === 'embroidery') {
        setupFee = toCents(window.NewLogoConfig?.prices?.digitalisationCents) || 1800;
      }
    }

    if (isInclusiveBasis) {
      extraPerItem = normaliseInclusiveCostCents(extraPerItem);
      setupFee = normaliseInclusiveCostCents(setupFee);
    }

    // Written by the customiser for the cart transform. Older baskets predate
    // it, so fall back to grossing up the exclusive figure ourselves.
    let extraPerItemGross = toCents(source._logo_extra_per_item_gross_cents);
    if (extraPerItemGross <= 0) extraPerItemGross = grossUpCents(extraPerItem);

    return {
      extraPerItem,
      extraPerItemGross,
      setupFee,
    };
  }

  function isDigitisationFeeItem(item) {
    if (!item) return false;
    const variantId = String(item.variant_id ?? item.id ?? '').trim();
    return variantId === DIGITISATION_FEE_VARIANT_ID_STR;
  }

  function resolveLegacyLogoIdentity(properties) {
    const source = properties || {};
    const method = String(source._logo_method || source['Logo Method'] || '').trim().toLowerCase();
    const sizeTier = String(source._logo_size_tier || source['Logo Size'] || '').trim().toLowerCase();
    if (!method) return '';

    const artworkUrls = String(source._logo_artwork_urls || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    if (!artworkUrls.length) {
      [source._logo_artwork_url, source._logo_artwork, source._logo_artwork_2, source._logo_artwork_3]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
        .forEach((value) => artworkUrls.push(value));
    }
    const artworkNames = String(source._logo_artwork_filenames || source._logo_artwork_filename || '')
      .split('|')
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    const artworkSignature = artworkUrls.join('|') || artworkNames.join('|');
    const signature = `${artworkSignature}|${method}|${sizeTier}`.trim();
    return signature ? `legacy-logo:${signature}` : '';
  }

  function isSetupFeeRequiredForItem(item, index) {
    if (!item || isDigitisationFeeItem(item)) return null;
    const properties = item.properties || {};
    const logoType = String(properties._logo_type || properties['Logo Type'] || '').trim().toLowerCase();
    if (logoType && logoType !== 'logo') return null;
    const setupRequiredValue = String(properties._logo_setup_required || properties['Logo Setup Required'] || '').toLowerCase();
    const setupRequired = setupRequiredValue === 'yes' || setupRequiredValue === 'true' || setupRequiredValue === '1';
    const explicitGroupSetupFee = toCents(properties._logo_setup_group_fee_cents);
    const hasSetupSignal = setupRequired || explicitGroupSetupFee > 0;
    if (!hasSetupSignal) return null;

    const explicitSetupFee = toCents(properties._logo_setup_fee_cents);
    const method = String(properties._logo_method || properties['Logo Method'] || '').trim().toLowerCase();
    const fallbackSetupFee = setupRequired && method === 'embroidery' ? toCents(window.NewLogoConfig?.prices?.digitalisationCents) || 1800 : 0;
    const effectiveSetupFee = explicitGroupSetupFee > 0 ? explicitGroupSetupFee : explicitSetupFee > 0 ? explicitSetupFee : fallbackSetupFee;
    if (effectiveSetupFee <= 0) return null;
    const explicitSetupUnits = toWholeNumber(properties._logo_setup_units);
    const setupUnits = explicitSetupUnits > 0 ? explicitSetupUnits : 1;

    const explicitToken = String(properties._logo_customiser_token || '').trim();
    if (explicitToken) return { token: explicitToken, units: setupUnits };

    const legacyIdentity = resolveLegacyLogoIdentity(properties);
    if (legacyIdentity) return { token: legacyIdentity, units: setupUnits };

    const lineKey = String(item.key || '').trim();
    if (lineKey) return { token: lineKey, units: setupUnits };

    const groupToken = String(properties._logo_customisation_group || '').trim();
    return { token: groupToken || `line-${index}`, units: setupUnits };
  }

  /**
   * How many digitisation fees the basket needs: one per unique logo.
   *
   * `_logo_setup_hashes` carries a content-derived identity per fee-bearing
   * logo, so the same artwork counts once no matter how many lines or separate
   * customiser sessions it spans. Lines written before that property existed
   * fall back to the old token grouping, which is right within one session and
   * only over-counts across sessions as it always did.
   */
  function getRequiredDigitisationFeeQuantity(items) {
    const logoIdentities = new Set();
    const legacyGroups = new Map();

    (items || []).forEach((item, index) => {
      const result = isSetupFeeRequiredForItem(item, index);
      if (!result || !result.token) return;

      const hashes = String(item?.properties?._logo_setup_hashes || '')
        .split('|')
        .map((entry) => entry.trim())
        .filter(Boolean);

      if (hashes.length) {
        hashes.forEach((hash) => logoIdentities.add(hash));
        return;
      }

      const units = Number.parseInt(result.units, 10) || 1;
      legacyGroups.set(result.token, Math.max(legacyGroups.get(result.token) || 0, units));
    });

    const legacyTotal = Array.from(legacyGroups.values()).reduce((sum, units) => sum + units, 0);
    return logoIdentities.size + legacyTotal;
  }

  function getDigitisationFeeLineQuantity(items) {
    return (items || []).reduce((sum, item) => {
      if (!isDigitisationFeeItem(item)) return sum;
      return sum + toWholeNumber(item.quantity);
    }, 0);
  }

  function setControlLockState(element, shouldLock) {
    if (!element) return;
    const hasOriginalState = Object.prototype.hasOwnProperty.call(element.dataset, 'digitisationOriginalDisabled');
    if (!shouldLock && !hasOriginalState) return;

    if (!hasOriginalState) {
      element.dataset.digitisationOriginalDisabled = element.disabled ? 'true' : 'false';
    }

    if (shouldLock) {
      element.disabled = true;
      element.setAttribute('aria-disabled', 'true');
      return;
    }

    const originalDisabled = element.dataset.digitisationOriginalDisabled === 'true';
    element.disabled = originalDisabled;
    if (!originalDisabled) {
      element.removeAttribute('aria-disabled');
    }
    delete element.dataset.digitisationOriginalDisabled;
  }

  function setAnchorLockState(anchor, shouldLock) {
    if (!anchor) return;
    const hasOriginalState = Object.prototype.hasOwnProperty.call(anchor.dataset, 'digitisationOriginalTabindex');
    if (!shouldLock && !hasOriginalState) return;

    if (!hasOriginalState) {
      anchor.dataset.digitisationOriginalTabindex = anchor.hasAttribute('tabindex')
        ? anchor.getAttribute('tabindex')
        : '__none__';
      anchor.dataset.digitisationOriginalPointerEvents = anchor.style.pointerEvents || '';
      anchor.dataset.digitisationOriginalOpacity = anchor.style.opacity || '';
    }

    if (shouldLock) {
      anchor.setAttribute('aria-disabled', 'true');
      anchor.setAttribute('tabindex', '-1');
      anchor.style.pointerEvents = 'none';
      anchor.style.opacity = '0.45';
      return;
    }

    const originalTabindex = anchor.dataset.digitisationOriginalTabindex;
    if (originalTabindex === '__none__') {
      anchor.removeAttribute('tabindex');
    } else {
      anchor.setAttribute('tabindex', originalTabindex);
    }

    anchor.style.pointerEvents = anchor.dataset.digitisationOriginalPointerEvents || '';
    anchor.style.opacity = anchor.dataset.digitisationOriginalOpacity || '';
    anchor.removeAttribute('aria-disabled');
    delete anchor.dataset.digitisationOriginalTabindex;
    delete anchor.dataset.digitisationOriginalPointerEvents;
    delete anchor.dataset.digitisationOriginalOpacity;
  }

  function setDigitisationLineLockedState(itemEl, shouldLock) {
    if (!itemEl) return;
    if (shouldLock) {
      itemEl.dataset.digitisationFeeLocked = 'true';
    } else {
      delete itemEl.dataset.digitisationFeeLocked;
    }

    const quantityControls = itemEl.querySelectorAll('.quantity__button, .quantity__input, input[name="updates[]"]');
    quantityControls.forEach((control) => setControlLockState(control, shouldLock));

    const removeLinks = itemEl.querySelectorAll('.cart-item__remove, [is="cart-remove-item"], a.btn-remove');
    removeLinks.forEach((anchor) => setAnchorLockState(anchor, shouldLock));
  }

  function applyDigitisationLineLocks(cart, requiredQty) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const itemElements = Array.from(document.querySelectorAll('.cart-item'));
    const elementMap = buildCartItemMapByElement(itemElements, items);

    itemElements.forEach((itemEl) => {
      const matched = elementMap.get(itemEl) || null;
      const cartItem = matched?.item || null;
      const shouldLock = requiredQty > 0 && isDigitisationFeeItem(cartItem);
      setDigitisationLineLockedState(itemEl, shouldLock);
      if (cartItem?.key) {
        itemEl.setAttribute('data-cart-item-key', String(cartItem.key));
      }
    });
  }

  function isCartDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    return Boolean(drawer && drawer.hasAttribute('open'));
  }

  function clearSetupFeeRows() {
    document.querySelectorAll('.new-logo-setup-row').forEach((row) => row.remove());
  }

  function notifyCartStateUpdated(cart) {
    if (!cart || typeof cart !== 'object') return;

    if (
      window.FoxTheme &&
      window.FoxTheme.pubsub &&
      window.FoxTheme.pubsub.publish &&
      window.FoxTheme.pubsub.PUB_SUB_EVENTS &&
      window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate
    ) {
      window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: CART_PRICING_SOURCE,
        cart,
      });
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart,
          source: CART_PRICING_SOURCE,
        },
      })
    );
  }

  /**
   * The section ids the theme wants re-rendered after a cart mutation. Both
   * cart-drawer and cart-items answer this, so the bundle covers whichever is
   * on the page.
   */
  function getCartSectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }

  /**
   * Hands rendered section HTML to the theme's own renderer.
   *
   * Without this the fee line was added or removed server-side while its row
   * stayed on screen until the next page load: applyCartDisplayPricing only
   * rewrites text on rows that already exist, and the theme only re-renders
   * from a `sections` payload it gets back from a cart mutation.
   */
  function publishRenderedCartState(parsedState) {
    if (!parsedState || typeof parsedState !== 'object') return false;
    const pubsub = window.FoxTheme?.pubsub;
    const event = pubsub?.PUB_SUB_EVENTS?.cartUpdate;
    if (!pubsub?.publish || !event) return false;
    if (!parsedState.sections) return false;

    pubsub.publish(event, { cart: parsedState, source: CART_PRICING_SOURCE });
    return true;
  }

  /**
   * Cart writes retried with backoff against Shopify's 429 throttle.
   *
   * update.js sets absolute quantities, so replaying it is safe. add.js is not
   * idempotent — it increments — so it is only replayed on 429, where the
   * request was rejected outright and nothing was added. A 5xx there is
   * ambiguous and could double the fee line, so it is left alone.
   */
  async function cartWrite(url, options, { retryServerErrors = true, attempts = 3 } = {}) {
    let response = null;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      response = await fetch(url, options);
      if (response.ok) return response;
      const worthRetrying = response.status === 429 || (retryServerErrors && response.status >= 500);
      if (!worthRetrying || attempt === attempts - 1) return response;
      const after = Number.parseFloat(response.headers.get('Retry-After') || '');
      const waitMs = Number.isFinite(after) && after > 0
        ? Math.min(after * 1000, 8000)
        : Math.min(700 * 2 ** attempt, 5000);
      await new Promise((resolve) => setTimeout(resolve, waitMs));
    }
    return response;
  }

  async function addDigitisationFeeVariant(quantity) {
    if (quantity <= 0) return null;
    const response = await cartWrite('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: DIGITISATION_FEE_VARIANT_ID,
            quantity,
          },
        ],
        sections: getCartSectionsToBundle(),
      }),
    }, { retryServerErrors: false });

    if (!response.ok) {
      throw new Error('Unable to add digitalisation fee item.');
    }

    return response.json().catch(() => null);
  }

  async function setDigitisationFeeVariantQuantity(quantity) {
    const response = await cartWrite('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [DIGITISATION_FEE_VARIANT_ID]: Math.max(0, toWholeNumber(quantity)),
        },
        sections: getCartSectionsToBundle(),
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to update digitalisation fee item.');
    }

    return response.json().catch(() => null);
  }

  let syncPromise = null;
  async function reconcileDigitisationFeeLine(cart) {
    let workingCart = cart;
    let safetyCounter = 0;

    while (safetyCounter < 6) {
      safetyCounter += 1;

      const items = Array.isArray(workingCart?.items) ? workingCart.items : [];
      const requiredQty = getRequiredDigitisationFeeQuantity(items);
      const currentQty = getDigitisationFeeLineQuantity(items);

      if (requiredQty === currentQty) {
        applyDigitisationLineLocks(workingCart, requiredQty);
        return workingCart;
      }

      // If another reconcile is active, wait, refetch, and re-evaluate against latest cart.
      if (syncPromise) {
        await syncPromise;
        workingCart = await fetchCart();
        continue;
      }

      syncPromise = (async () => {
        const mutationState =
          requiredQty > currentQty
            ? await addDigitisationFeeVariant(requiredQty - currentQty)
            : await setDigitisationFeeVariantQuantity(requiredQty);
        // During a bracketed operation the caller renders once, immediately
        // after this returns. Rendering here as well made the drawer fetch its
        // sections twice for one removal — pure cost, since the second render
        // supersedes this one a few hundred milliseconds later.
        const operationOwnsRender = isCartOperationBusy();
        const rendered = operationOwnsRender ? true : publishRenderedCartState(mutationState);

        const refreshedCart = await fetchCart();
        applyDigitisationLineLocks(refreshedCart, requiredQty);

        document.dispatchEvent(
          new CustomEvent('new-logo:digitisation-fee-reconciled', {
            detail: {
              requiredQty,
              cart: refreshedCart,
            },
          })
        );
        notifyCartStateUpdated(refreshedCart);

        // Adding or removing the fee changes which lines exist, and
        // applyCartDisplayPricing can only rewrite text on rows that are
        // already in the DOM — it cannot delete a row for a line that has
        // gone. Without this the fee row lingered until the next page load.
        //
        // `force` and the `new-logo-explicit` source both bypass
        // new-logo-cart-refresh-guard.js. This cannot loop: the re-render's
        // cart:updated triggers another reconcile, which finds the quantities
        // already in agreement and returns before reaching this branch.
        if (!rendered) {
          // No section HTML came back (older theme paths). cart:refresh only
          // re-renders the drawer, but it is better than a stale row.
          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: {
                open: isCartDrawerOpen(),
                force: true,
                reason: 'digitisation-fee-reconciled',
                source: 'new-logo-explicit',
              },
            })
          );
        }

        return refreshedCart;
      })();

      try {
        workingCart = await syncPromise;
      } finally {
        syncPromise = null;
      }
    }

    // Failsafe: return latest known server cart if reconciliation kept changing.
    const fallbackCart = await fetchCart();
    const fallbackRequiredQty = getRequiredDigitisationFeeQuantity(Array.isArray(fallbackCart?.items) ? fallbackCart.items : []);
    applyDigitisationLineLocks(fallbackCart, fallbackRequiredQty);
    return fallbackCart;
  }

  function applyCartDisplayPricing(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    syncCartDrawerEmptyState(cart);
    if (!items.length) {
      document.querySelectorAll('.new-logo-extra-note').forEach((el) => el.remove());
      clearSetupFeeRows();
      const emptyTotal = toCents(cart?.total_price);
      document.querySelectorAll('.totals__subtotal-value').forEach((subtotalElement) => {
        subtotalElement.textContent = formatMoney(emptyTotal);
      });
      return;
    }

    const requiredDigitisationFeeQty = getRequiredDigitisationFeeQuantity(items);
    applyDigitisationLineLocks(cart, requiredDigitisationFeeQty);

    const itemElements = Array.from(document.querySelectorAll('.cart-item'));
    const elementMap = buildCartItemMapByElement(itemElements, items);

    itemElements.forEach((itemEl) => {
      const matched = elementMap.get(itemEl) || null;
      const item = matched?.item || null;
      if (!item) return;
      if (item?.key) {
        itemEl.setAttribute('data-cart-item-key', String(item.key));
      }
      if (isDigitisationFeeItem(item)) return;
      const properties = item?.properties || {};
      const costParts = getLogoCostParts(properties);
      const extraPerItem = costParts.extraPerItem;

      // Shopify's line prices already carry the logo cost: the cart transform
      // function adds it server-side, so item.final_price / final_line_price
      // and cart.total_price come back inflated already.
      //
      // This script used to add it on top, which is what made the basket read
      // higher than the checkout. Leave the theme's own prices alone and just
      // annotate what is included.
      // Match whichever basis the line price beside it is showing. Cart lines
      // do now honour the Ex/Inc VAT toggle, so pinning this to the inclusive
      // figure made the note disagree with the price it annotates: a line
      // reading £104.32 ex VAT was captioned £4.19 inc.
      const pricesElement = itemEl.querySelector('.cart-item__prices');
      upsertCustomisationNote(
        itemEl,
        pricesElement,
        vatDisplayIncludesTax() ? costParts.extraPerItemGross : extraPerItem
      );
    });

    // Subtotal likewise: cart.total_price is already transformed.

    clearSetupFeeRows();
  }

  async function fetchCart() {
    const response = await fetch(`/cart.js?_=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart JSON');
    }

    return response.json();
  }

  let pendingTimer = null;
  let pendingCartPayload = null;
  let refreshInFlight = false;

  function getEventCartPayload(candidate) {
    if (!candidate || typeof candidate !== 'object') return null;
    if (!Array.isArray(candidate.items)) return null;
    return candidate;
  }

  let inFlightRun = null;
  // A refresh asked for while a pass was already running. Without this, a
  // hint-less request (VAT toggle, cart:refresh) during a pass was silently
  // dropped and the captions kept the old figures until some later event.
  let rerunRequested = false;
  // A refresh suppressed by the busy flag is replayed once the operation ends
  // — see the cart-operation-end listener in init().
  let suppressedWhileBusy = false;

  function runRefresh(cartHint = null, force = false) {
    if (!force && isCartOperationBusy()) {
      suppressedWhileBusy = true;
      return Promise.resolve();
    }
    if (refreshInFlight) {
      const queued = getEventCartPayload(cartHint);
      if (queued) pendingCartPayload = queued;
      else rerunRequested = true;
      // A forced caller — the operation asking for the fee to be settled —
      // waits for the run already in progress. Returning here unconditionally
      // meant reconcileNow() resolved instantly having reconciled nothing, and
      // the operation carried on as though the fee were settled.
      return force && inFlightRun ? inFlightRun : Promise.resolve();
    }

    const run = runRefreshInner(cartHint).finally(() => {
      // Only clear our own registration: the queued re-run in the inner
      // finally may already have installed a newer one, and clearing that
      // made a forced reconcileNow() resolve instantly having done nothing.
      if (inFlightRun === run) inFlightRun = null;
    });
    inFlightRun = run;
    return run;
  }

  async function runRefreshInner(cartHint = null) {
    refreshInFlight = true;
    try {
      // Never paint from the event hint, only from the server.
      //
      // Queued cart:updated events carry payloads of different vintages. The
      // old code painted the hint first for speed, then repainted from a fresh
      // fetch — so removing a customisation showed it vanish, come back (stale
      // hint), then vanish again when the real cart landed. That is the double
      // refresh. Fetching first costs nothing extra: whenever a hint existed
      // this fetched anyway, so this is the same request count and one paint
      // fewer. The old empty-hint guard is gone with it — an empty hint can no
      // longer flash "no items" if no hint is ever painted.
      const cart = await fetchCart();
      applyCartDisplayPricing(cart);
      emitCartPricingSynced('completed-initial');

      const authoritativeCart = cart;
      const reconciledCart = await reconcileDigitisationFeeLine(authoritativeCart);
      if (reconciledCart && reconciledCart !== cart) {
        applyCartDisplayPricing(reconciledCart);
      }

      emitCartPricingSynced('completed');
    } catch (error) {
      console.error(error);
      emitCartPricingSynced('error');
    } finally {
      refreshInFlight = false;
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      const rerun = rerunRequested;
      rerunRequested = false;
      if (queued) {
        runRefresh(queued);
      } else if (rerun) {
        runRefresh(null);
      }
    }
  }

  /**
   * True while new-logo-inject.js has a cart operation open.
   *
   * A removal is several writes, and each one emitted cart:updated, which woke
   * this module, which reconciled the fee, which emitted more — a measured
   * ~3 second storm of events and repaints for one click. While an operation
   * owns the cart, this module stays out of the way; the operation calls
   * reconcileNow() once at the point it actually wants the fee settled.
   */
  function isCartOperationBusy() {
    return document.documentElement.getAttribute('data-new-logo-cart-busy') === '1';
  }

  function scheduleRefresh(delay = 80, cartHint = null, force = false) {
    if (!force && isCartOperationBusy()) return;
    const eventCart = getEventCartPayload(cartHint);
    if (eventCart) {
      pendingCartPayload = eventCart;
    }

    if (pendingTimer) clearTimeout(pendingTimer);
    if (delay <= 0) {
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      runRefresh(queued, force);
      return;
    }

    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      const queued = pendingCartPayload;
      pendingCartPayload = null;
      runRefresh(queued, force);
    }, delay);
  }

  /**
   * Re-render the drawer whenever it opens.
   *
   * The digitisation fee settles asynchronously — it can climb while several
   * lines are being configured and is corrected a moment later. The drawer
   * keeps whatever it last rendered, so it could sit showing a stale quantity
   * (Qty 3) until the page was refreshed even though the cart held 1.
   */
  function watchCartDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__newLogoOpenWatcher) return;
    drawer.__newLogoOpenWatcher = true;

    let wasOpen = drawer.hasAttribute('open');
    const observer = new MutationObserver(() => {
      const isOpen = drawer.hasAttribute('open');
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      if (!isOpen) return;
      // A save opens the drawer mid-operation; the operation runs its own
      // reconcile and render, and a competing forced refresh here was one of
      // the visible double-paints.
      if (isCartOperationBusy()) return;

      // Only re-render when the drawer is actually showing the wrong fee.
      // Refreshing on every open re-rendered the section needlessly, and each
      // re-render briefly showed the empty-cart state before the items came
      // back — the flash customers were seeing.
      fetchCart()
        .then((cart) => {
          const items = Array.isArray(cart?.items) ? cart.items : [];
          const serverFeeQty = getDigitisationFeeLineQuantity(items);
          const shownFeeQty = Array.from(
            drawer.querySelectorAll(`.cart-item[data-variant-id="${DIGITISATION_FEE_VARIANT_ID_STR}"]`)
          ).reduce((sum, row) => {
            const value = toWholeNumber(row.querySelector('.quantity__input')?.value);
            return sum + (value > 0 ? value : 0);
          }, 0);

          if (serverFeeQty === shownFeeQty) {
            applyCartDisplayPricing(cart);
            return;
          }

          document.dispatchEvent(
            new CustomEvent('cart:refresh', {
              detail: {
                open: false,
                force: true,
                reason: 'drawer-fee-out-of-date',
                source: 'new-logo-explicit',
              },
            })
          );
        })
        .catch(() => {});
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // The only way in while an operation is open. Resolves when the cart has been
  // reconciled and repainted, so the caller can sequence its own render after.
  window.NewLogoCartPricing = Object.assign(window.NewLogoCartPricing || {}, {
    reconcileNow() {
      // runRefresh now chains a forced call onto any run already in progress,
      // so this is a single call either way.
      return runRefresh(null, true);
    },

    // Paint-only. Step 1 paints the captions and fee locks onto the pre-render
    // DOM, then the render replaces that DOM wholesale and every later repaint
    // request is swallowed by the busy guard — so the numbers corrected
    // themselves only on some later unrelated cart event, after the cover had
    // lifted. This repaints over the finished render, once.
    repaintNow(cart) {
      if (cart) {
        applyCartDisplayPricing(cart);
        return Promise.resolve();
      }
      return runRefresh(null, true);
    },
  });

  function init() {
    scheduleRefresh(0);
    watchCartDrawerOpen();
    if (window.customElements?.whenDefined) {
      window.customElements.whenDefined('cart-drawer').then(watchCartDrawerOpen).catch(() => {});
    }

    document.addEventListener('cart:updated', (event) => {
      if (String(event?.detail?.source || '') === CART_PRICING_SOURCE) return;
      // new-logo-inject.js announces the settled cart once an operation ends.
      // It has already reconciled and repainted via reconcileNow(), so acting
      // on this would repeat the whole cycle right after the drawer opens.
      if (String(event?.detail?.source || '') === 'new-logo-settled') return;
      scheduleRefresh(0, event?.detail?.cart || null);
    });
    document.addEventListener('cart:refresh', () => {
      pendingCartPayload = null;
      scheduleRefresh(120);
    });

    // Whatever the busy flag suppressed is superseded by the operation's own
    // reconcile + repaint — except requests that arrived during the tail
    // (e.g. the VAT toggle clicked mid-operation). Replay once, after the end,
    // so captions and the empty state settle against the final cart.
    document.addEventListener('new-logo:cart-operation-end', () => {
      if (!suppressedWhileBusy) return;
      suppressedWhileBusy = false;
      scheduleRefresh(80);
    });

    // The VAT toggle does not change the cart, so nothing else would repaint
    // the note. It writes localStorage in its own handler, hence the tick.
    document.addEventListener(
      'click',
      (event) => {
        if (!event.target?.closest?.('[data-vat-toggle-switch]')) return;
        scheduleRefresh(20);
      },
      true
    );
    document.addEventListener('logo-customiser:saved', () => scheduleRefresh(220));
    window.addEventListener('logo-customiser:saved', () => scheduleRefresh(220));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The drawer's injected digitisation panel needs the same answer this script
  // gives, or the two disagree on screen. Exposed rather than duplicated.
  window.NewLogoCartPricing = window.NewLogoCartPricing || {};
  window.NewLogoCartPricing.getRequiredDigitisationFeeQuantity = getRequiredDigitisationFeeQuantity;
})();
